import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Text, Box } from '@cad-arc';
import { EventPanelFormType, EventPanelOptions } from '@mark43/cad-api';
import { NoMembersIndicator } from '../../../tabs/components/no-members-indicator';
import { EntityPanelFormGroup } from '../../../../panels/entity-panels/entity-panel-components';
import { TabCard } from '../../../tabs/components/tab-card';
import { ConditionalWrapper } from '../../../../common/components/conditional-wrapper';
import { PropertyBaseKey } from '../types';
import { useIsClearedEvent } from '../../use-is-cleared-event';
import { FormInputButton } from '../../../../forms/integrated-form-components/form-button';
import { useTrackedFieldArray } from '../../../../forms/use-tracked-field-array';
import { getDefaultDrug, getDefaultFirearm, getDefaultOtherItem } from './property-helper';
import { PropertyFieldset } from './event-panel-form-properties-fieldset';
export type EventPanelFormPropertyFieldsProps = {
    form: UseFormReturn<EventPanelFormType>;
    eventPanelOptions: EventPanelOptions;
    showTitle?: boolean;
    baseKey: PropertyBaseKey;
    noMembersText?: string;
    displayAsCards?: boolean;
    hideDexFields?: boolean;
} & (
    | {
          allowMembersControl: true;
          addButtonText?: string;
      }
    | {
          allowMembersControl?: false;
      }
);

export function EventPanelFormPropertyFields(
    props: EventPanelFormPropertyFieldsProps
): React.ReactElement {
    const eventForm = props.form;
    const { fields, append, remove } = useTrackedFieldArray({
        control: eventForm.control,
        name: props.baseKey,
        keyName: 'rhfKey',
    });
    const eventId = eventForm.getValues().eventId;
    const isClearedEvent = useIsClearedEvent();

    const appendItem = () => {
        if (props.baseKey === 'firearms' || props.baseKey === 'mergedFirearms') {
            append(getDefaultFirearm());
        } else if (props.baseKey === 'drugs' || props.baseKey === 'mergedDrugs') {
            append(getDefaultDrug());
        } else if (props.baseKey === 'otherItems' || props.baseKey === 'mergedOtherItems') {
            append(getDefaultOtherItem());
        }
    };

    const addPropertyButton = props.allowMembersControl && !isClearedEvent && (
        <FormInputButton
            size="md"
            variant="ghost"
            leadingVisual="Add"
            data-testid={`add-${props.baseKey}-button`}
            onClick={appendItem}
        >
            {props.addButtonText}
        </FormInputButton>
    );

    const noMemberIcon =
        props.baseKey === 'firearms' || props.baseKey === 'mergedFirearms' ? 'Firearm' : 'Property';

    return (
        <EntityPanelFormGroup data-testid="property-form-section">
            {props.showTitle && fields.length > 0 ? (
                <Text variant="headingXs">Property</Text>
            ) : undefined}
            {fields.length === 0 && props.noMembersText ? (
                <NoMembersIndicator
                    text={props.noMembersText}
                    icon={noMemberIcon}
                    action={addPropertyButton}
                    value={fields}
                />
            ) : (
                <>
                    {fields.map((field, index) => (
                        <ConditionalWrapper
                            key={field.rhfKey}
                            condition={props.displayAsCards}
                            wrapper={(children) => <TabCard>{children}</TabCard>}
                        >
                            <PropertyFieldset
                                form={eventForm}
                                field={field}
                                index={index}
                                remove={remove}
                                baseKey={props.baseKey as PropertyBaseKey}
                                eventPanelOptions={props.eventPanelOptions}
                                allowMembersControl={props.allowMembersControl}
                                eventId={eventId}
                                displayAsCards={props.displayAsCards}
                            />
                        </ConditionalWrapper>
                    ))}

                    <Box>{addPropertyButton}</Box>
                </>
            )}
        </EntityPanelFormGroup>
    );
}
